import { useEffect, useMemo, useRef } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  CircularProgress,
  Divider,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { RefreshPageButton } from "components/RefreshPageButton";
import { ClientAppEvent } from "constants/index";
import { useSentFollowRequests, useShipperShipmentPageContext } from "hooks";
import { MODAL_STYLE } from "layouts/constants";

import { ShipmentProgressDetailTableRow } from "./ShipmentProgressDetailTableRow";
import {
  getShipmentDetailTableTitle,
  sortByCarrierShipmentProgressStatus,
} from "./utils";

interface Props {
  shipment: ShipperCondensedShipment | undefined;
  progress: ShipmentProgress | null;
  open: boolean;
  onClose: VoidFunction;
}

export function ShipmentProgressDetail(props: Props) {
  const { shipment, progress, open, onClose } = props;
  const { onRefresh } = useShipperShipmentPageContext();
  const posthog = usePostHog();

  // This component is mounted with the modal closed, so the fetch waits for
  // `open` rather than hitting the endpoint on every shipment page render.
  const {
    sentFollowRequests: followShipperRequests,
    loading: followLoading,
    error: followError,
  } = useSentFollowRequests({ enabled: open });

  const sortedCarrierProgress = useMemo(
    () =>
      progress
        ? sortByCarrierShipmentProgressStatus(
            progress.network_progress.concat(progress.match_progress),
          )
        : [],
    [progress],
  );

  // Fires exactly once per shipment per open (not on every progress refresh
  // while the modal stays open), and waits for progress if it is not loaded
  // by the time the modal opens. Keyed by shipment id rather than a bare
  // boolean: the parent can swap in a different shipment while this stays
  // mounted, and progress for the previous shipment can still be the one
  // in props when that happens -- progress.shipment (not just shipment.info.id
  // being set) is what confirms the two have actually caught up with each
  // other.
  const hasCapturedOpenForShipmentIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) {
      hasCapturedOpenForShipmentIdRef.current = null;
      return;
    }
    const openedShipmentId = shipment?.info.id;
    if (
      openedShipmentId == null ||
      progress?.shipment !== openedShipmentId ||
      hasCapturedOpenForShipmentIdRef.current === openedShipmentId
    ) {
      return;
    }
    hasCapturedOpenForShipmentIdRef.current = openedShipmentId;
    posthog?.capture(ClientAppEvent.MATCH_TABLE_OPENED, {
      shipment_id: openedShipmentId,
      // This is the shipper's own company, since it is their shipment -- no
      // single carrier is implicated by opening the whole match table, so
      // there is no carrier_company_id to attach here.
      shipper_company_id: shipment?.shipment_company.id,
      view_count: progress.view_count,
      quote_count: progress.quote_count,
      no_capacity_count: progress.no_capacity_count,
    });
  }, [open, shipment, progress, posthog]);

  if (
    !open ||
    !shipment ||
    // ShipmentProgressContainer keeps the previous shipment's progress in
    // state while it fetches the newly selected one's, so this can otherwise
    // render shipment A's counts and carrier statuses under shipment B's
    // header.
    progress?.shipment !== shipment.info.id
  ) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={MODAL_STYLE}>
        <Stack justifyContent="flex-end" direction="row">
          <CloseIcon
            sx={{
              "&:hover": {
                cursor: "pointer",
              },
            }}
            onClick={onClose}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5">
            {getShipmentDetailTableTitle(shipment, progress)}
          </Typography>
          <RefreshPageButton
            id="shipper-shipment-overview-refresh"
            iconButton
            hardReload={false}
            onRefresh={onRefresh ?? (() => {})}
          />
        </Stack>
        <Divider sx={{ my: "1rem" }} />
        <ErrorTypography error={followError} />
        {followLoading ? (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <TableContainer
            id="table-container"
            component={Paper}
            sx={{ overflowY: "auto", maxHeight: "500px" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contact</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCarrierProgress.map((carrierProgress) => (
                  <ShipmentProgressDetailTableRow
                    key={carrierProgress.carrier.user.id}
                    carrierProgress={carrierProgress}
                    followShipperRequestsSent={followShipperRequests ?? null}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Modal>
  );
}
